import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <main className="pt-20 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl text-foreground-900 mb-2">漫画未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">您访问的漫画可能已下架或链接有误</p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回漫画列表
          </Link>
        </div>
      </main>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.categorySlug)?.name || comic.category;
  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.categorySlug === comic.categorySlug || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 6);

  const seoTitle = `${comic.title} - ${comic.author} - 韩漫吧免费韩漫在线阅读`;
  const seoDesc = `${comic.title}是由${comic.author}创作的${categoryName}类韩国漫画。${comic.description.slice(0, 120)}... 在韩漫吧免费在线阅读最新章节。`;

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanba.com' },
          { '@type': 'ListItem', position: 2, name: `${categoryName}漫画`, item: `https://hanmanba.com/category/${comic.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: comic.title },
        ],
      },
      {
        '@type': 'Article',
        headline: `${comic.title} - ${comic.author}`,
        description: comic.description,
        datePublished: comic.createdAt,
        dateModified: comic.updatedAt,
        author: { '@type': 'Person', name: comic.author },
        publisher: { '@type': 'Organization', name: '韩漫吧' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://hanmanba.com/article/${comic.id}` },
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={seoTitle}
        description={seoDesc}
        keywords={`${comic.title},${comic.author},${categoryName}漫画,韩国漫画,韩漫吧,免费韩漫,在线阅读`}
        canonicalUrl={`https://hanmanba.com/article/${comic.id}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <main className="pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: `${categoryName}漫画`, to: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            {/* Cover Image */}
            <div className="w-full md:w-64 shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                  title={`${comic.title} - ${comic.author} - 韩漫吧免费韩国漫画在线阅读`}
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700">
                  {categoryName}
                </span>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-accent-100 text-accent-700">
                  <i className="ri-star-fill text-[10px]"></i>
                  {comic.rating}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  <strong className="text-foreground-700">{comic.author}</strong>
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-3-line"></i>
                  共 {comic.chapters.length} 话
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-0.5 rounded-full text-[11px] bg-background-100 text-foreground-600 border border-background-200/70">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {comic.description}
              </p>

              <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-400">
                <span>创建于 {comic.createdAt}</span>
                <span>·</span>
                <span>最近更新 {comic.updatedAt}</span>
              </div>
            </div>
          </div>

          {/* Chapter List */}
          <section className="mb-12">
            <h2 className="font-heading text-xl text-foreground-900 mb-5 flex items-center gap-2">
              <i className="ri-book-2-line text-accent-500"></i>
              章节列表
              <span className="text-sm font-normal text-foreground-500">（共 {comic.chapters.length} 话）</span>
            </h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="divide-y divide-background-200/70">
                {comic.chapters.map((chapter, index) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold shrink-0">
                        {chapter.number}
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                          {chapter.title}
                        </h3>
                        <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] px-2 py-0.5 rounded bg-accent-100 text-accent-700 font-medium">
                        免费
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section>
              <h2 className="font-heading text-xl text-foreground-900 mb-5 flex items-center gap-2">
                <i className="ri-links-line text-accent-500"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}